import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Media Upload Categories
// ---------------------------------------------------------------------------

export const MEDIA_CATEGORIES = [
  "crm_attachment",    // attached to CRM contact/deal/company
  "email_image",       // embedded in email campaigns
  "email_attachment",  // file attachment on emails
  "profile_photo",     // user or contact profile photo
  "logo",              // tenant logo / branding
  "form_upload",       // form submission file upload
  "page_asset",        // page builder asset (supplements pageAssets for non-page contexts)
  "document",          // general document (proposals, contracts)
  "chat_attachment",   // attached to chat/conversation messages
  "other",             // uncategorized
] as const;

export type MediaCategory = (typeof MEDIA_CATEGORIES)[number];

export const UPLOAD_MEDIA_TYPES = [
  "image",
  "video",
  "audio",
  "document",
  "archive",
  "other",
] as const;

export type UploadMediaType = (typeof UPLOAD_MEDIA_TYPES)[number];

// ---------------------------------------------------------------------------
// Media Uploads — General-purpose file registry
// ---------------------------------------------------------------------------

export const mediaUploads = pgTable(
  "media_uploads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    r2Key: text("r2_key").notNull(),                     // R2 object key (ease-media bucket)
    r2Bucket: text("r2_bucket").notNull().default("ease-media"),
    filename: text("filename").notNull(),                 // original filename
    contentType: text("content_type").notNull(),          // MIME type
    sizeBytes: integer("size_bytes").notNull(),
    mediaType: text("media_type").notNull(),              // "image" | "video" | etc.
    category: text("category").notNull().default("other"),
    // Dimensions (images/video)
    width: integer("width"),
    height: integer("height"),
    durationSeconds: integer("duration_seconds"),          // audio/video
    // Entity linkage (polymorphic — at most one FK set)
    entityType: text("entity_type"),                      // "contact", "deal", "company", "email", "page", "form_submission"
    entityId: uuid("entity_id"),                          // FK to the linked entity
    // Metadata
    uploadedBy: uuid("uploaded_by"),                      // user who uploaded
    isPublic: boolean("is_public").notNull().default(false), // publicly accessible URL?
    thumbnailR2Key: text("thumbnail_r2_key"),             // auto-generated thumbnail
    altText: text("alt_text"),                            // accessibility
    // Lifecycle
    deletedAt: timestamp("deleted_at", tz),               // soft delete for undo
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_media_uploads_tenant").on(table.tenantId),
    index("idx_media_uploads_entity").on(table.entityType, table.entityId),
    index("idx_media_uploads_category").on(table.tenantId, table.category),
    check(
      "chk_media_uploads_type",
      sql`${table.mediaType} IN ('image', 'video', 'audio', 'document', 'archive', 'other')`,
    ),
  ],
);
